import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { EnforcementSchedule, TimeRange } from "types";

import React from "react";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TimeField } from "@mui/x-date-pickers";

export interface EnforcementCalendarProps {
  schedule: EnforcementSchedule;
  loading?: boolean;
  hideTitle?: boolean;
}

const EnforcementCalendar: React.FC<EnforcementCalendarProps> = ({
  schedule,
  loading = false,
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("lg"));
  return (
    <React.Fragment>
      {!hideTitle && (
        <Divider orientation="horizontal">
          <Typography variant={"overline"} align={"center"}>
            {t("rule.data.enforcementSchedule.label")}
          </Typography>
        </Divider>
      )}
      <Stack
        direction={mobile ? "column" : "row"}
        spacing={mobile ? 0 : 1}
        sx={{
          borderRight: "1px solid black",
          borderLeft: "1px solid black",
          borderTop: hideTitle ? "1px solid black" : "none",
          borderBottom: hideTitle ? "1px solid black" : "none",
          borderRadius: "4px",
        }}
        px={{ sm: 16, lg: 0 }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {Object.keys(schedule).map((d) => {
          const day = schedule[d.toLowerCase() as keyof EnforcementSchedule];
          return (
            <Stack
              key={d}
              flexDirection={mobile ? "row" : "column"}
              flex={1}
              display={"flex"}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 0.5 },
              }}
            >
              <Box sx={{ flex: 1, height: "100%" }}>
                <Typography
                  variant="h6"
                  align={mobile ? "left" : "center"}
                  sx={{ pl: { xs: 2, lg: 0 } }}
                  textTransform={"capitalize"}
                >
                  {`${t(d)}${mobile ? ":" : ""}`}
                </Typography>
              </Box>

              {day && day.length ? (
                day.map((tr: TimeRange, i: number) => {
                  const start = dayjs(tr.startTime, "HHmm");
                  const end = dayjs(tr.endTime, "HHmm");
                  return (
                    <Stack
                      key={i}
                      direction="row"
                      sx={{ flex: 2, height: "100%" }}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="body1"
                        sx={{ flex: 1, textAlign: "center" }}
                      >
                        {start.format("hh:mma")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ flex: 1, textAlign: "center", px: 1 }}
                      >
                        {"-"}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ flex: 1, textAlign: "center" }}
                      >
                        {end.format("hh:mma")}
                      </Typography>
                    </Stack>
                  );
                })
              ) : (
                <Box sx={{ flex: 2, height: "100%" }}>
                  <Typography
                    variant={mobile ? "body1" : "body2"}
                    align={mobile ? "right" : "center"}
                    py={{ xs: 0.25, lg: 0 }}
                  >
                    {loading ? (
                      <Skeleton
                        width={"100%"}
                        variant={"text"}
                        sx={{
                          borderBottom: "1px solid black",
                          pb: 1,
                          fontSize: "1rem",
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </Typography>
                  {mobile && (
                    <Divider sx={{ borderColor: "rgba(0,0,0, 0.42)" }} />
                  )}
                </Box>
              )}
            </Stack>
          );
        })}
      </Stack>
    </React.Fragment>
  );
};

export default EnforcementCalendar;
